import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Helmet } from "react-helmet-async";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions | Agota Estate</title>
        <meta name="description" content="Terms and Conditions for Agota Estate - Read our terms of service, disclaimers, and legal information." />
      </Helmet>
      
      <Navbar />
      
      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-8">Terms & Conditions</h1>
            <p className="text-muted-foreground mb-8">Last updated: December 2024</p>
            
            <div className="prose prose-lg max-w-none space-y-8 text-foreground/90">
              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">1. Acceptance of Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  By accessing and using the Agota Estate website, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website or services.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">2. Service Description</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Agota Estate operates as a luxury real estate consultancy, connecting discerning clients with premium properties through our network of partner agencies including Sotheby's International Realty, Christie's International Real Estate, Knight Frank, and Savills. We facilitate introductions and provide advisory services but do not directly own, sell, or lease properties.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">3. Property Listings Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>IMPORTANT:</strong> The property listings displayed on this website are for illustrative and informational purposes only.
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Property details, prices, specifications, and availability are subject to change without notice.</li>
                  <li>All information should be independently verified before making any decisions.</li>
                  <li>Agota Estate does not guarantee the accuracy, completeness, or reliability of any listing information.</li>
                  <li>Actual properties may differ from descriptions and images shown.</li>
                  <li>All transactions are conducted through our partner agencies who maintain their own terms and conditions.</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">4. AI-Generated Images Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  <strong>NOTICE:</strong> This website contains AI-generated images for illustrative purposes.
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Some property images displayed are computer-generated and do not represent actual properties.</li>
                  <li>AI-generated images are used for conceptual visualization and marketing purposes only.</li>
                  <li>These images should not be relied upon for making purchasing or investment decisions.</li>
                  <li>Actual property images will be provided upon request through our partner agencies.</li>
                  <li>We clearly indicate when images are AI-generated where applicable.</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">5. Partner Agency Relationships</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Agota Estate collaborates with leading global real estate agencies. Each partner agency operates independently and maintains its own policies, terms, and conditions. Any transaction facilitated through our introductions will be governed by the respective partner agency's terms. Agota Estate is not liable for the actions, representations, or omissions of partner agencies.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">6. No Professional Advice</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The content on this website does not constitute legal, financial, tax, or investment advice. Users should consult qualified professionals before making any real estate decisions. Property values and market conditions can fluctuate, and past performance is not indicative of future results.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">7. Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed">
                  All content on this website, including text, graphics, logos, and images (except partner agency logos used with permission), is the property of Agota Estate or its content suppliers. Unauthorized reproduction, distribution, or modification is prohibited.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">8. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To the fullest extent permitted by law, Agota Estate shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of this website or reliance on any information provided. This includes but is not limited to damages for loss of profits, goodwill, data, or other intangible losses.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">9. Indemnification</h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to indemnify and hold harmless Agota Estate, its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of the website or violation of these terms.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">10. Third-Party Links</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our website may contain links to third-party websites, including our partner agencies. We are not responsible for the content, privacy policies, or practices of these external sites.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">11. Modifications</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Agota Estate reserves the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting. Continued use of the website constitutes acceptance of modified terms.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">12. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms and Conditions shall be governed by and construed in accordance with applicable laws. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the competent courts.
                </p>
              </section>

              <section>
                <h2 className="font-display text-2xl text-foreground mb-4">13. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions regarding these Terms and Conditions, please contact us at legal@agotaestate.com or through our contact form.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </>
  );
};

export default Terms;
